import { useCallback, useEffect } from 'react';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

/**
 * Event fires dynamic custom form submitted.
 * @memberOf Playtech.API.events
 * @event dynamic-custom-form-submit
 * @type {callback}
 * @param {Object} details
 * @param {Object} details.formValues contains form field values (fieldName: fieldValue)
 * @param {Object} details.formIMSValues contains form field values (fieldIMSName: fieldValue)
 * @param {string} details.formName contains form name that was picked in component settings
 * @param {function} details.resolve {function} function that should be called in case of success. Accepts text or a translation key to be shown as notification message
 * @param {function} details.reject {function} function that should be called in case of error. Accepts text or a translation key to be shown as form error
 * @example Playtech.on('dynamic-custom-form-submit', ({ formValues, formIMSValues, formName, resolve, reject }) => {
 *     const { userName } = formValues;
 *
 *     if (formName !== 'my-custom-form') {
 *         return;
 *     }
 *
 *     if (userName === 'HanSolo'){
 *        console.log(userName)
 *        resolve('Login is successful!')
 *     } else {
 *        reject('This form could be submitted only by Han Solo')
 *     }
 * });
 */

export const EVENT_NAME = 'dynamic-custom-form-submit';

export const useOnDynamicCustomFormSubmit = (eventTarget: EventTarget) => {
    const handleEvent = useCallback((response): void => {
        const event = new CustomEvent(EVENT_NAME, { detail: response });

        eventTarget.dispatchEvent(event);
    }, [eventTarget]);

    useEffect(() => {
        publicEventsAPIService.on(EVENT_NAME, handleEvent);

        return () => publicEventsAPIService.off(EVENT_NAME, handleEvent);
    }, [handleEvent]);
};
